import { AlertBox, Box, Button, FlexBox, Icon, Typography, getTokenCssVar } from '@vp/swan';
import { __ } from '@99designs/i18n';
import type { ChatErrorCode } from '../../client/types';

type ChatLoadErrorCode = Extract<
    ChatErrorCode,
    'unauthorized' | 'forbidden' | 'not_found' | 'internal_error' | 'service_unavailable'
>;

type ChatLoadErrorAction = 'none' | 'refresh' | 'retry';

type ErrorPresentation = {
    message: string;
    action: ChatLoadErrorAction;
};

const LOAD_ERROR_CODES: Record<ChatLoadErrorCode, true> = {
    unauthorized: true,
    forbidden: true,
    not_found: true,
    internal_error: true,
    service_unavailable: true,
};

export interface ChatLoadErrorBannerProps {
    errorCode: ChatErrorCode | null;
    onRetry: () => void;
    reloadBudgetExhausted: boolean;
}

function isChatLoadErrorCode(errorCode: ChatErrorCode): errorCode is ChatLoadErrorCode {
    return errorCode in LOAD_ERROR_CODES;
}

function basePresentationFor(errorCode: ChatErrorCode | null): ErrorPresentation {
    if (errorCode === null || !isChatLoadErrorCode(errorCode)) {
        return {
            message: __("We couldn't load your chat — try again"),
            action: 'retry',
        };
    }

    const presentations: Record<ChatLoadErrorCode, ErrorPresentation> = {
        unauthorized: {
            message: __('Your session has expired — refresh the page to continue'),
            action: 'refresh',
        },
        forbidden: {
            message: __("You don't have access to this brief"),
            action: 'none',
        },
        not_found: {
            message: __('This chat is no longer available'),
            action: 'none',
        },
        internal_error: {
            message: __("We couldn't load your chat — try again"),
            action: 'retry',
        },
        service_unavailable: {
            message: __('The chat service is busy — try again in a moment'),
            action: 'retry',
        },
    };

    return presentations[errorCode];
}

function presentationFor(
    errorCode: ChatErrorCode | null,
    reloadBudgetExhausted: boolean
): ErrorPresentation {
    const presentation = basePresentationFor(errorCode);

    // Terminal codes must outrank a spent budget: "still having trouble loading" would
    // be a lie for a brief the customer has no access to, or that no longer exists.
    if (reloadBudgetExhausted && presentation.action === 'retry') {
        return {
            message: __("We're still having trouble loading your chat"),
            action: 'refresh',
        };
    }

    return presentation;
}

export function ChatLoadErrorBanner({
    errorCode,
    onRetry,
    reloadBudgetExhausted,
}: ChatLoadErrorBannerProps) {
    const { message, action } = presentationFor(errorCode, reloadBudgetExhausted);

    const handleAction = () => {
        if (action === 'retry') {
            onRetry();
            return;
        }

        if (action === 'refresh') {
            window.location.reload();
        }
    };

    const actionLabel =
        action === 'retry' ? __('Try again') : action === 'refresh' ? __('Refresh page') : null;

    return (
        <Box
            style={{
                width: '100%',
                paddingBottom: getTokenCssVar('SwanSemSpace4'),
            }}
        >
            <AlertBox skin="error" toast={false} accessibleTextForIcon={__('Error')}>
                <FlexBox alignItems="flex-start" justifyContent="space-between" gap="4">
                    <Typography fontSkin="body-standard">{message}</Typography>
                    {actionLabel && (
                        <Button skin="link" onClick={handleAction}>
                            <FlexBox alignItems="center" gap="3">
                                <Box style={{ flexShrink: 0 }}>
                                    <Icon iconType="reset" size="small" />
                                </Box>
                                <Box>{actionLabel}</Box>
                            </FlexBox>
                        </Button>
                    )}
                </FlexBox>
            </AlertBox>
        </Box>
    );
}
